import React, { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Drawer, { PanelSection, PanelInput } from "./Drawer";
import { roomColor } from "@/components/schedule/FilterBar";
import { crewApi } from "@/lib/api";
import { confirm } from "@/lib/dialog";

export default function CrewPanel({ open, onClose, projectId, crew, rooms, onMutated }) {
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState({
    name: "", role: "", primary_room_id: "", max_shift_min: 720, min_turnaround_min: 600, skills: "",
  });
  const [busy, setBusy] = useState(false);

  const roomOptions = [{ value: "", label: "—" }, ...rooms.map((r) => ({ value: r.id, label: `${r.abbreviation} · ${r.name}` }))];

  const startAdd = async () => {
    if (!adding.name.trim()) return;
    setBusy(true);
    try {
      await crewApi.create(projectId, {
        name: adding.name.trim(),
        role: adding.role.trim(),
        primary_room_id: adding.primary_room_id || null,
        max_shift_min: adding.max_shift_min || 720,
        min_turnaround_min: adding.min_turnaround_min || 600,
        skills: adding.skills.split(",").map((s) => s.trim()).filter(Boolean),
      });
      setAdding({ name: "", role: "", primary_room_id: "", max_shift_min: 720, min_turnaround_min: 600, skills: "" });
      onMutated && (await onMutated());
      toast.success("Crew added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (c, updates) => {
    try {
      await crewApi.update(c.id, updates);
      onMutated && (await onMutated());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (c) => {
    const ok = await confirm({
      title: `Remove crew member "${c.name}"?`,
      body: "Existing assignments to this crew member will be removed.",
      confirmLabel: "REMOVE",
      danger: true,
    });
    if (!ok) return;
    try {
      await crewApi.remove(c.id);
      onMutated && (await onMutated());
      toast.success("Crew member removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const roomById = Object.fromEntries(rooms.map((r) => [r.id, r]));

  return (
    <Drawer open={open} onClose={onClose} title="CREW" count={crew.length} dataTestid="crew-panel" width={460}>
      <PanelSection title="ROSTER">
        <div className="space-y-2">
          {crew.map((c) => {
            const room = roomById[c.primary_room_id];
            return (
              <CrewRow
                key={c.id}
                crew={c}
                roomColor={roomColor(c.color_token || room?.color_token)}
                roomLabel={room ? `${room.abbreviation}` : "—"}
                roomOptions={roomOptions}
                editing={editing === c.id}
                onStartEdit={() => setEditing(c.id)}
                onCancelEdit={() => setEditing(null)}
                onSave={(updates) => { saveEdit(c, updates); setEditing(null); }}
                onRemove={() => remove(c)}
              />
            );
          })}
        </div>
      </PanelSection>

      <PanelSection title="+ NEW CREW">
        <PanelInput label="NAME" value={adding.name} onChange={(v) => setAdding({ ...adding, name: v })} placeholder="Pat Sample" dataTestid="crew-name-input" />
        <PanelInput label="ROLE" value={adding.role} onChange={(v) => setAdding({ ...adding, role: v })} placeholder="LX Op" />
        <PanelInput label="PRIMARY ROOM" value={adding.primary_room_id} onChange={(v) => setAdding({ ...adding, primary_room_id: v })} options={roomOptions} />
        <div className="grid grid-cols-2 gap-2">
          <PanelInput label="MAX SHIFT (MIN)" type="number" value={adding.max_shift_min} onChange={(v) => setAdding({ ...adding, max_shift_min: v })} />
          <PanelInput label="MIN TURNAROUND (MIN)" type="number" value={adding.min_turnaround_min} onChange={(v) => setAdding({ ...adding, min_turnaround_min: v })} />
        </div>
        <PanelInput label="SKILLS (comma sep)" value={adding.skills} onChange={(v) => setAdding({ ...adding, skills: v })} placeholder="audio, foh, monitor" />
        <button onClick={startAdd} disabled={busy || !adding.name.trim()} className="btn-primary w-full justify-center" data-testid="crew-add-btn">
          <Plus className="w-3 h-3" /> ADD CREW
        </button>
      </PanelSection>
    </Drawer>
  );
}

function CrewRow({ crew, roomColor, roomLabel, roomOptions, editing, onStartEdit, onCancelEdit, onSave, onRemove }) {
  const [draft, setDraft] = useState({ name: crew.name, role: crew.role, primary_room_id: crew.primary_room_id || "", max_shift_min: crew.max_shift_min, min_turnaround_min: crew.min_turnaround_min, skills: (crew.skills || []).join(", ") });
  React.useEffect(() => {
    if (editing) setDraft({ name: crew.name, role: crew.role, primary_room_id: crew.primary_room_id || "", max_shift_min: crew.max_shift_min, min_turnaround_min: crew.min_turnaround_min, skills: (crew.skills || []).join(", ") });
  }, [editing, crew]);

  if (!editing) {
    return (
      <div className="flex items-center gap-2 p-2 border border-line bg-bg-elevated hover:border-line-strong group">
        <span className="w-1 self-stretch" style={{ background: roomColor }} />
        <div className="flex-1 min-w-0">
          <div className="text-xs text-ink truncate">{crew.name}</div>
          <div className="mono text-2xs uppercase tracking-wider text-ink-mute mt-0.5">
            {roomLabel} · {crew.role || "—"} · {(crew.skills || []).slice(0, 3).join(" / ") || "no skills"}
          </div>
        </div>
        <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity">
          <button onClick={onStartEdit} className="btn-ghost">EDIT</button>
          <button onClick={onRemove} className="text-ink-mute hover:text-red"><Trash2 className="w-3 h-3" /></button>
        </div>
      </div>
    );
  }
  return (
    <div className="p-2 border border-amber bg-bg-elevated space-y-2">
      <PanelInput label="NAME" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} />
      <PanelInput label="ROLE" value={draft.role} onChange={(v) => setDraft({ ...draft, role: v })} />
      <PanelInput label="PRIMARY ROOM" value={draft.primary_room_id} onChange={(v) => setDraft({ ...draft, primary_room_id: v })} options={roomOptions} />
      <div className="grid grid-cols-2 gap-2">
        <PanelInput label="MAX SHIFT" type="number" value={draft.max_shift_min} onChange={(v) => setDraft({ ...draft, max_shift_min: v })} />
        <PanelInput label="MIN TURNAROUND" type="number" value={draft.min_turnaround_min} onChange={(v) => setDraft({ ...draft, min_turnaround_min: v })} />
      </div>
      <PanelInput label="SKILLS" value={draft.skills} onChange={(v) => setDraft({ ...draft, skills: v })} />
      <div className="flex gap-1">
        <button
          onClick={() => onSave({
            ...draft,
            primary_room_id: draft.primary_room_id || null,
            skills: draft.skills.split(",").map((s) => s.trim()).filter(Boolean),
          })}
          className="btn-primary flex-1 justify-center"
        >SAVE</button>
        <button onClick={onCancelEdit} className="btn-ghost">CANCEL</button>
      </div>
    </div>
  );
}
